import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { ArrowPathIcon, PlusIcon, XMarkIcon } from '@heroicons/react/16/solid'
import { useSystemQuery } from 'hooks/use-system-query'
import { useEffect, useRef, useState } from 'react'
import Button from '../../../components/tailwind/Button'
import { Heading } from '../../../components/ui/Heading'
import trackEvents from '../../../constants/track-events'
import {
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../../context'
import { isSet, truncateCareerID } from '../../../helpers'
import { trackEvent } from '../../../helpers/analytics'
import { useCopyToClipboard } from '../../../hooks/use-copy-to-clipboard'
import { useTalentsQuery } from '../../../hooks/use-talents-query'
import { useDiscover } from '../hooks/useDiscover'
import { useOnboardingAssistant } from '../hooks/useOnboardingAssistant'
import { useSelectionParams } from '../hooks/useSelectionParams'
import StancedLeftActive from '../svg/stanced-left-active.svg'
import StancedLeft from '../svg/stanced-left.svg'
import StancedRightActive from '../svg/stanced-right-active.svg'
import StancedRightDefault from '../svg/stanced-right-default.svg'
import StancedRight from '../svg/stanced-right.svg'
import {
  MESSAGE_ROLES,
  MESSAGE_TYPES,
  sendSmartMessageSequence,
  startMatchingProfileIntroAssistantFlow
} from '../utils'
import { AssistantSpotlight } from './AssistantSpotlight'
import CareerBadge from './CareerBadge'
import TalentProfileJoyride from './joyride/TalentProfileJoyride'
import MatchStepConnector from './MatchStepConnector'
import { TalentBadge } from './talent'

export default function MatchOverviewSection({
  className,
  variant,
  career,
  isLoadingCareer,
  talent,
  disableRender
}) {
  const { t, i18n } = useTranslation()
  const {
    setAssistantIsTyping,
    sendMessageToAssistant,
    enableAssistant,
    hideAssistant,
    setAddCareerModalOpen,
    setInviteTalentOpen,
    setAssessmentPreviewModalOpen,
    setEnableClickOnFirstDimension,
    assistantSpotlightSelector
  } = useDiscover()
  const language = i18n.language
  const navigate = useNavigate()
  const tempCareerId = useRef(null)
  const { addCareer, removeTalent, removeCareer } = useSelectionParams()
  const { activeSpace, refetchActiveSpace, cognitoUser } = useUserContext()
  const { data: system } = useSystemQuery({
    username: cognitoUser?.username
  })

  const [runTalentProfileJoyride, setRunTalentProfileJoyride] = useState(false)
  const [run, setRun] = useState(false)

  // hält die Talente-Query aktiv wie bisher, die Daten nutzt nur der
  // auskommentierte Joyride-Block unten
  useTalentsQuery()

  const hasClosedJoyride = useRef(false)

  const { partner, refetchPartner } = usePartnerContext()
  const { success, error } = useNotificationContext()
  const { copy } = useCopyToClipboard()
  const hasCompletedOnboarding = activeSpace?.hasCompletedOnboarding

  const copyId = async (id) => {
    if (await copy(id)) success(t('common.copy_success'))
    else error(t('global.copy_error'))
  }

  const talentName = talent?.person
    ? `${talent.person.firstname || talent.person.invitation_firstname} ${talent.person.lastname || talent.person.invitation_lastname}`
    : t('common.unknown_talent')

  // Start onboarding flow directly (previously triggered after closing welcome video)
  useEffect(() => {
    setTimeout(() => {
      setRun(true)
    }, 500)
  }, [])

  useOnboardingAssistant({
    variant,
    hasCompletedOnboarding,
    activeSpace,
    enableAssistant,
    hideAssistant,
    setAssistantIsTyping,
    sendMessageToAssistant,
    partner,
    refetchPartner,
    refetchActiveSpace,
    language,
    cognitoUser,
    system,
    run,
    setAssessmentPreviewModalOpen
  })

  const onClickCreateCareer = () => {
    trackEvent(trackEvents.DISCOVER_PRESSED_CREATE_CAREER)

    if (hasCompletedOnboarding) {
      setAddCareerModalOpen(true)
      return
    }

    enableAssistant()

    sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 1,
              title: t(
                'discover.assistant.system_message.create_career.continue.title'
              ),
              description: t(
                'discover.assistant.system_message.create_career.continue.description'
              ),
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT
            }),
          textLength: t(
            'discover.assistant.system_message.create_career.continue.description'
          ).length
        },
        {
          fn: () =>
            sendMessageToAssistant({
              id: 2,
              type: MESSAGE_TYPES.OPTION,
              role: MESSAGE_ROLES.ASSISTANT,
              options: [
                {
                  title: t(
                    'discover.assistant.system_message.create_career.options.1.title'
                  ),
                  description: t(
                    'discover.assistant.system_message.create_career.options.1.description'
                  ),
                  recommended: true,
                  onClick: () => {
                    trackEvent(
                      trackEvents.DISCOVER_PRESSED_CREATE_CAREER_OPTION_1
                    )
                    setTimeout(() => {
                      navigate('/discover/create-career')
                    }, 750)
                  },
                  time: t(
                    'discover.assistant.system_message.create_career.options.1.time'
                  )
                },
                {
                  title: t(
                    'discover.assistant.system_message.create_career.options.2.title'
                  ),
                  description: t(
                    'discover.assistant.system_message.create_career.options.2.description'
                  ),
                  recommended: false,
                  onClick: () => {
                    trackEvent(
                      trackEvents.DISCOVER_PRESSED_CREATE_CAREER_OPTION_2
                    )

                    localStorage.setItem(
                      'career-creation-return',
                      JSON.stringify({
                        returnType: 'career'
                      })
                    )

                    setTimeout(() => {
                      navigate('/career/new', {
                        replace: true
                      })
                    }, 750)
                  },
                  time: t(
                    'discover.assistant.system_message.create_career.options.2.time'
                  )
                }
              ]
            })
        }
      ]
    })
  }

  const onClickInviteTalent = () => {
    trackEvent(trackEvents.DISCOVER_PRESSED_INVITE_TALENT)
    if (hasCompletedOnboarding) {
      setInviteTalentOpen(true)
    }
  }

  const onClickCloseCareer = (careerId) => {
    if (!hasCompletedOnboarding) return
    trackEvent(trackEvents.DISCOVER_PRESSED_CLOSE_CAREER)
    if (isSet(talent)) {
      removeCareer()
    } else {
      navigate('/discover')
    }
  }

  if (disableRender) {
    return (
      <>
        <AssistantSpotlight
          selector={assistantSpotlightSelector}
          scrollOffset={60}
        />
      </>
    )
  }

  return (
    <div className={clsx('pt-4', className)}>
      <Heading level={2} className={'text-darkBlue'}>
        {variant === 'onboarding'
          ? t('discover.match_overview_section.title')
          : t('discover.match_overview_section.initial')}
      </Heading>
      <MatchStepConnector className={'mt-4'}>
        <div
          id='requirement-profile-career-overview'
          style={{
            width: '407px',
            height: '205px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            position: 'relative',
            backgroundImage: `url(${career ? StancedLeft : StancedLeftActive})`
          }}
        >
          {career && (
            <div className='flex h-full flex-col justify-between py-5 pl-6 pr-5'>
              <div>
                <div className='flex flex-row justify-between'>
                  <div className='flex flex-row items-center'>
                    <CareerBadge />
                    <span
                      className='ml-4 max-w-[280px] truncate text-[22px] font-semibold text-darkBlack'
                      title={career.title}
                    >
                      {career.title}
                    </span>
                  </div>
                  <XMarkIcon
                    className='size-4 cursor-pointer text-darkGrey'
                    onClick={() => {
                      onClickCloseCareer(career.id)
                    }}
                  />
                </div>
                <div className='mt-6'>
                  <button
                    type='button'
                    className='group cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600'
                    onClick={() => copyId(career.id)}
                    title={`${t('discover.match_overview_section.career_id_full')}: ${career.id} (${t('discover.match_overview_section.click_to_copy')})`}
                  >
                    <span className='text-base font-normal text-rockGrey transition-colors group-hover:text-primaryBlue'>
                      {t('discover.match_overview_section.career_id', {
                        career_id: truncateCareerID(career.id)
                      })}
                    </span>
                  </button>
                </div>
              </div>

              {hasCompletedOnboarding && (
                <button
                  className='flex flex-row items-center'
                  onClick={() => {
                    removeCareer(career.id)
                    setAddCareerModalOpen(true)
                  }}
                >
                  <span className='text-sm font-medium text-primaryBlue'>
                    {t('discover.match_overview_section.change_career')}
                  </span>
                  <ArrowPathIcon className='ml-1 size-4 text-primaryBlue' />
                </button>
              )}
            </div>
          )}
          {!isSet(career) && (
            <div className='flex h-full flex-col items-start justify-between px-8 py-6'>
              <div>
                <Heading level={3}>
                  {t('discover.match_overview_section.create_career.initial')}
                </Heading>
                <p className='mt-2 w-full text-sm/snug text-darkGrey md:w-60'>
                  {t(
                    'discover.match_overview_section.create_career.initial_description'
                  )}
                </p>
              </div>
              <Button.PrimaryXL
                text={t('discover.match_overview_section.create_career.button')}
                onClick={onClickCreateCareer}
                icon={<PlusIcon className='size-4' />}
                isPulsing={false}
              />
            </div>
          )}
        </div>
        <div
          id='requirement-profile-talent-overview'
          style={{
            width: '407px',
            height: '205px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            position: 'relative',
            backgroundImage: `url(${isSet(career) && !isSet(talent) ? StancedRightActive : talent ? StancedRight : StancedRightDefault})`
          }}
        >
          {talent && (
            <div className='flex h-full flex-col justify-between py-5 pl-12 pr-5'>
              <div>
                <div className='flex flex-row justify-between'>
                  <div className='flex flex-row items-center'>
                    <TalentBadge name={talentName} />
                    <span
                      className='ml-4 max-w-[280px] truncate text-[22px] font-semibold text-darkBlack'
                      title={talentName}
                    >
                      {talentName}
                    </span>
                  </div>
                  <XMarkIcon
                    className='size-4 cursor-pointer text-darkGrey'
                    onClick={() => {
                      removeTalent(talent.id)
                    }}
                  />
                </div>
                <div className='mt-6'>
                  <button
                    type='button'
                    className='group cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600'
                    onClick={() => copyId(talent.id)}
                    title={`${t('discover.match_overview_section.talent_id_full')}: ${talent.id} (${t('discover.match_overview_section.click_to_copy')})`}
                  >
                    <span className='text-base font-normal text-rockGrey transition-colors group-hover:text-primaryBlue'>
                      {t('discover.match_overview_section.talent_id', {
                        talent_id: truncateCareerID(talent.id)
                      })}
                    </span>
                  </button>
                </div>
              </div>

              {hasCompletedOnboarding && (
                <button
                  className='flex flex-row items-center'
                  onClick={() => {
                    removeTalent(talent.id)
                    setInviteTalentOpen(true)
                  }}
                >
                  <span className='text-sm font-medium text-primaryBlue'>
                    {t('discover.match_overview_section.change_talent')}
                  </span>
                  <ArrowPathIcon className='ml-1 size-4 text-primaryBlue' />
                </button>
              )}
            </div>
          )}

          {!isSet(talent) && (
            <div className='flex h-full flex-col items-start justify-between px-12 py-6'>
              <div>
                <Heading level={3}>
                  {t('discover.match_overview_section.invite_talent.title')}
                </Heading>
                <p className='mt-2 w-full text-sm/snug text-darkGrey'>
                  {t(
                    'discover.match_overview_section.invite_talent.description'
                  )}
                </p>
              </div>
              <Button.SecondaryXL
                text={t('discover.match_overview_section.invite_talent.button')}
                onClick={onClickInviteTalent}
                icon={<PlusIcon className='size-4' />}
                isPulsing={false}
                disabled={!hasCompletedOnboarding}
              />
            </div>
          )}
        </div>
      </MatchStepConnector>
      <AssistantSpotlight
        selector={assistantSpotlightSelector}
        scrollOffset={60}
      />
      {/* <RequirementProfileJoyride
        run={runRequirementProfileJoyride}
        onClose={() => {
          if (hasClosedJoyride.current) return
          hasClosedJoyride.current = true
          setRunRequirementProfileJoyride(false)
          setTimeout(() => {
            setAssessmentPreviewModalOpen(true)
          }, 350)

          return

          enableAssistant(true)

          sendSmartMessageSequence({
            setIsTyping: setAssistantIsTyping,
            messages: [
              {
                fn: () =>
                  sendMessageToAssistant({
                    id: 'requirement-profile-intro-1',
                    description:
                      'Lasse uns gemeinsam herausfinden, wie gut ein Talent zu der Stelle passt.',
                    role: MESSAGE_ROLES.ASSISTANT,
                    type: MESSAGE_TYPES.TEXT
                  }),
                textLength: 2000
              },
              {
                fn: () =>
                  sendMessageToAssistant({
                    id: 'requirement-profile-intro-2',
                    title: 'Lernen wir Charlie kennen',
                    description:
                      'Aivy macht die Fähigkeiten, Persönlichkeit, Werte und Interessen sichtbar – und zeigt den Menschen hinter dem Lebenslauf.',
                    role: MESSAGE_ROLES.ASSISTANT,
                    type: MESSAGE_TYPES.TEXT
                  }),
                textLength: 2000
              },
              {
                fn: () =>
                  sendMessageToAssistant({
                    id: 'requirement-profile-intro-3',
                    description:
                      'Angenommen, eine Bewerbung ist eingegangen: Aivy zeigt das Stärkenprofil – mit Fähigkeiten, Persönlichkeit, Werten und Interessen. So wird der Mensch hinter dem Lebenslauf sichtbar.',
                    role: MESSAGE_ROLES.ASSISTANT,
                    type: MESSAGE_TYPES.TEXT,
                    cta: {
                      title: 'Stärken anzeigen',
                      isPulsing: true,
                      onClick: () => {
                        hasClosedJoyride.current = false
                        hideAssistant()
                        tempCareerId.current = career?.id
                        updateSelection([talents.spaces[0].id], [])
                        setTimeout(() => {
                          setRunTalentProfileJoyride(true)
                        }, 1500)
                      }
                    }
                  }),
                textLength: 2000
              }
            ]
          })
        }}
      /> */}
      <TalentProfileJoyride
        run={runTalentProfileJoyride}
        onClose={() => {
          if (hasClosedJoyride.current) return
          hasClosedJoyride.current = true

          setRunTalentProfileJoyride(false)
          enableAssistant(true)

          sendSmartMessageSequence({
            setIsTyping: setAssistantIsTyping,
            messages: [
              {
                fn: () =>
                  sendMessageToAssistant({
                    id: 'talent-profile-intro-1',
                    title: 'Passung auf einen Blick',
                    description:
                      'Aivy vergleicht die Stärken von Charlie mit den Anforderungen der Stelle. Du erhältst einen übersichtlichen Passungs-Score sowie die wichtigsten Einflussfaktoren. Sieh direkt, wie gut das Talent passt, welche Eigenschaften die Passung fördern und ob entscheidende Muss-Kriterien erfüllt sind.',
                    role: MESSAGE_ROLES.ASSISTANT,
                    type: MESSAGE_TYPES.TEXT,
                    cta: {
                      title: 'Passung anzeigen',
                      isPulsing: true,
                      onClick: () => {
                        hasClosedJoyride.current = false
                        addCareer(tempCareerId.current)

                        startMatchingProfileIntroAssistantFlow({
                          sendMessageToAssistant,
                          setAssistantIsTyping,
                          setEnableClickOnFirstDimension
                        })
                      }
                    }
                  }),
                textLength: 2000
              }
            ]
          })
        }}
      />
    </div>
  )
}
MatchOverviewSection.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(['onboarding', 'standard']).isRequired,
  career: PropTypes.object,
  isLoadingCareer: PropTypes.bool,
  talent: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    person: PropTypes.shape({
      firstname: PropTypes.string,
      invitation_firstname: PropTypes.string,
      lastname: PropTypes.string,
      invitation_lastname: PropTypes.string
    })
  }),
  disableRender: PropTypes.bool
}
